import { storeWrapper } from '../storeWrapper'
import { debounce } from '@repo/utils/debounce'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { useToastStore } from '../useToastStore/useToastStore'

const initialState = {
  expenses: [],
  expensesTableUpdate: 0,
  expenseClaimPeriodStart: null
}

export const useExpensesStore = storeWrapper('useExpensesStore', (set, get) => ({
  ...initialState,
  createExpenseWithLocation: async ({
    claimRecipientUserId,
    clientNo,
    description,
    expenseClaimLocationsJson,
    expenseClaimTypeId,
    grossAmount,
    incurredDate,
    isRecoverable,
    isVatApplicable,
    journeyType,
    matterNo,
    miles,
    netAmount,
    receipt,
    receiptContentType,
    receiptFilename,
    receiptFileSize,
    vatAmount
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'createExpenseWithLocation',
        delay: 600,
        callback: async () => {
          const form = new FormData()

          claimRecipientUserId && form.append('Model.ClaimRecipientUserId', claimRecipientUserId)
          clientNo && form.append('Model.ClientNo', clientNo)
          description && form.append('Model.Description', description)
          expenseClaimLocationsJson && form.append('ExpenseClaimLocationsJson', expenseClaimLocationsJson)
          expenseClaimTypeId && form.append('Model.ExpenseClaimTypeId', expenseClaimTypeId)
          grossAmount != null && form.append('Model.GrossAmount', grossAmount)
          incurredDate && form.append('Model.IncurredDate', incurredDate)
          isRecoverable && form.append('Model.IsRecoverable', isRecoverable)
          form.append('Model.IsVatApplicable', isVatApplicable ?? false)
          journeyType && form.append('Model.JourneyType', journeyType)
          matterNo && form.append('Model.MatterNo', matterNo)
          miles && form.append('Model.Miles', miles)
          netAmount != null && form.append('Model.NetAmount', netAmount)
          receipt && form.append('Receipt', receipt)
          receiptContentType && form.append('Model.ReceiptContentType', receiptContentType)
          receiptFilename && form.append('Model.ReceiptFilename', receiptFilename)
          receiptFileSize && form.append('Model.ReceiptFileSize', receiptFileSize)
          form.append('Model.Status', 0)
          vatAmount != null && form.append('Model.VatAmount', vatAmount)

          try {
            const response = await ospreyAPI({
              headers: { 'Content-Type': 'multipart/form-data; charset=utf-8' },
              method: 'POST',
              service: 'accounts',
              endpoint: '/ExpenseClaim/CreateWithLocations',
              data: form,
              successToast: {
                type: 'success',
                title: 'Expense claim saved as draft',
                text: 'Remember to submit the claim for approval.'
              }
            })

            set({ expensesTableUpdate: get().expensesTableUpdate + 1 })
            resolve(response)
          } catch (error) {
            console.error('Error creating expense with location:', error)
            reject(error)
            return
          }
        }
      })
    })
  },
  updateExpenseWithLocation: async ({
    claimRecipientUserId,
    clientNo,
    description,
    expenseClaimId,
    expenseClaimLocationsJson,
    expenseClaimTypeId,
    grossAmount,
    incurredDate,
    postingDate,
    isRecoverable,
    isSubmitter,
    isVatApplicable,
    journeyType,
    matterNo,
    miles,
    netAmount,
    receipt,
    receiptContentType,
    receiptFilename,
    receiptFileSize,
    status,
    vatAmount
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: isSubmitter ? 'updateExpenseWithLocationSubmitter' : 'updateExpenseWithLocationApprover',
        delay: 600,
        callback: async () => {
          const form = new FormData()

          expenseClaimId && form.append('Model.ExpenseClaimId', expenseClaimId)
          claimRecipientUserId && form.append('Model.ClaimRecipientUserId', claimRecipientUserId)
          clientNo && form.append('Model.ClientNo', clientNo)
          description && form.append('Model.Description', description)
          expenseClaimLocationsJson && form.append('ExpenseClaimLocationsJson', expenseClaimLocationsJson)
          expenseClaimTypeId && form.append('Model.ExpenseClaimTypeId', expenseClaimTypeId)
          grossAmount != null && form.append('Model.GrossAmount', grossAmount)
          incurredDate && form.append('Model.IncurredDate', incurredDate)
          postingDate && form.append('Model.PostingDate', postingDate)
          isRecoverable && form.append('Model.IsRecoverable', isRecoverable)
          form.append('Model.IsVatApplicable', isVatApplicable ?? false)
          journeyType && form.append('Model.JourneyType', journeyType)
          matterNo && form.append('Model.MatterNo', matterNo)
          miles && form.append('Model.Miles', miles)
          netAmount != null && form.append('Model.NetAmount', netAmount)
          receipt && form.append('Receipt', receipt)
          receiptContentType && form.append('Model.ReceiptContentType', receiptContentType)
          receiptFilename && form.append('Model.ReceiptFilename', receiptFilename)
          receiptFileSize && form.append('Model.ReceiptFileSize', receiptFileSize)
          status != null && form.append('Model.Status', status)
          vatAmount != null && form.append('Model.VatAmount', vatAmount)
          try {
            const response = await ospreyAPI({
              headers: { 'Content-Type': 'multipart/form-data; charset=utf-8' },
              service: 'accounts',
              method: 'PUT',
              endpoint: isSubmitter
                ? '/ExpenseClaim/UpdateWithLocationsBySubmitter'
                : '/ExpenseClaim/UpdateWithLocationsByApprover',
              data: form,
              successToast: {
                type: 'success',
                title: 'Updated',
                text: 'Your expense claim has been updated.'
              }
            })

            set({ expensesTableUpdate: get().expensesTableUpdate + 1 })
            resolve(response)
          } catch (error) {
            console.error('Error updating expense with location:', error)
            reject(error)
            return
          }
        }
      })
    })
  },
  deleteExpenseClaimWithLocation: async ({ expenseClaimId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `deleteExpenseClaimWithLocation-${expenseClaimId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              service: 'accounts',
              method: 'DELETE',
              endpoint: '/ExpenseClaim/Delete',
              data: { expenseClaimId: expenseClaimId },
              sendAsUrlParams: true
            })

            set({ expensesTableUpdate: get().expensesTableUpdate + 1 })
            resolve(response)
          } catch (error) {
            console.error('Error deleting expense with location:', error)
            reject(error)
            return
          }
        }
      })
    })
  },
  updateExpenseClaimStatuses: async ({ status, expenseClaimIds, postingDate = null }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `updateExpenseClaimStatuses`,
        delay: 600,
        callback: async () => {
          try {
            // postingDate is optional and only supplied when moving claims to "Ready to pay" (5).
            let endpoint = `/ExpenseClaim/UpdateExpenseClaimStatuses?status=${status}`
            if (postingDate) endpoint += `&postingDate=${encodeURIComponent(postingDate)}`

            const response = await ospreyAPI({
              service: 'accounts',
              method: 'PUT',
              endpoint,
              data: expenseClaimIds
            })

            if (status === 5 && expenseClaimIds.length > 0) {
              const claimIdsToCheck = expenseClaimIds

              if (claimIdsToCheck.length > 0) {
                const wait = (ms) => new Promise((r) => setTimeout(r, ms))
                let allPosted = false

                for (let attempt = 0; attempt < 3; attempt++) {
                  await wait(1000)
                  try {
                    const results = await Promise.all(
                      claimIdsToCheck.map((id) =>
                        ospreyAPI({
                          method: 'GET',
                          service: 'accounts',
                          endpoint: '/ExpenseClaim/GetExtendedById',
                          data: { expenseClaimId: id }
                        })
                      )
                    )
                    allPosted = results.every((details) => details?.status === 6)
                    if (allPosted) break
                  } catch {
                    break
                  }
                }

                if (allPosted) {
                  const isPlural = claimIdsToCheck.length > 1
                  useToastStore.getState().showToast({
                    type: 'success',
                    title: isPlural ? 'Claims posted' : 'Claim posted',
                    text: isPlural
                      ? 'These expense claims have been posted automatically'
                      : 'This expense claim has been posted automatically'
                  })
                }
              }
            }

            set({ expensesTableUpdate: get().expensesTableUpdate + 1 })

            resolve(response)
          } catch (error) {
            console.error('Error updating expense statuses:', error)
            reject(error)
            return
          }
        }
      })
    })
  },
  fetchAllExtendedExpenses: async ({
    claimRecipientUserIds,
    clientNo,
    description,
    expenseClaimTypeDescription,
    grossAmountNumberComparisonOperator,
    grossAmountValue,
    hasReceipt,
    incurredDate,
    incurredDateComparisonOperator,
    isSubmission,
    isRecoverable,
    matterNo,
    orderByColumns,
    pageSize,
    startAtIndex,
    status
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'fetchAllExtendedExpenses',
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'accounts',
              endpoint: '/ExpenseClaim/GetAllExtendedBySearchCriteria',
              data: {
                ApproversOrSubmitters: isSubmission ? 'Submitters' : 'Approvers',
                ClaimRecipientUserIds: claimRecipientUserIds ?? null,
                ClientNo: clientNo ?? null,
                Description: description ?? null,
                ExpenseClaimTypeDescription: expenseClaimTypeDescription ?? null,
                HasReceipt: hasReceipt ?? null,
                GrossAmount:
                  grossAmountValue == null
                    ? null
                    : {
                        Value: grossAmountValue,
                        NumberComparisonOperator: grossAmountNumberComparisonOperator ?? 0
                      },
                IncurredDate:
                  incurredDate == null
                    ? null
                    : {
                        Date: incurredDate,
                        DateComparisonOperator: incurredDateComparisonOperator ?? 0
                      },
                IsRecoverable: isRecoverable ?? null,
                MatterNo: matterNo ?? null,
                OrderByColumns: orderByColumns ?? null,
                PageSize: pageSize ?? 25,
                StartAtIndex: startAtIndex ?? 0,
                Statuses: status ?? null
              }
            })

            resolve(response)
          } catch (error) {
            console.error('Error fetching extended expenses:', error)
            reject(error)
            return
          }
        }
      })
    })
  },
  fetchDraftExpenseClaims: async ({ pageSize, startAtIndex, status }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'fetchDraftExepenseClaims',
        delay: 600,
        callback: async () => {
          try {
            let statuses = null
            if (status != null) statuses = Array.isArray(status) ? status : [status]

            const response = await ospreyAPI({
              method: 'POST',
              service: 'accounts',
              endpoint: '/ExpenseClaim/GetAllExtendedBySearchCriteria',
              data: {
                ApproversOrSubmitters: 'Submitters',
                PageSize: pageSize ?? 25,
                StartAtIndex: startAtIndex ?? 0,
                Statuses: statuses
              }
            })

            resolve(response)
          } catch (err) {
            console.error('Error fetching draft expense claims:', err)
            reject(err)
            return
          }
        }
      })
    })
  },
  fetchAllExtendedExpensesAsCSV: async ({
    claimRecipientUserIds,
    clientNo,
    description,
    expenseClaimTypeDescription,
    grossAmountNumberComparisonOperator,
    grossAmountValue,
    hasReceipt,
    incurredDate,
    incurredDateComparisonOperator,
    isSubmission,
    isRecoverable,
    matterNo,
    orderByColumns,
    pageSize,
    startAtIndex,
    status
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'fetchAllExtendedExpenses',
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'accounts',
              endpoint: '/ExpenseClaim/GetAllExtendedBySearchCriteriaAsCsv',
              data: {
                ApproversOrSubmitters: isSubmission ? 'Submitters' : 'Approvers',
                ClaimRecipientUserIds: claimRecipientUserIds ?? null,
                ClientNo: clientNo ?? null,
                Description: description ?? null,
                ExpenseClaimTypeDescription: expenseClaimTypeDescription ?? null,
                GrossAmount:
                  grossAmountValue == null
                    ? null
                    : {
                        Value: grossAmountValue,
                        NumberComparisonOperator: grossAmountNumberComparisonOperator ?? 0
                      },
                HasReceipt: hasReceipt ?? null,
                IncurredDate:
                  incurredDate == null
                    ? null
                    : {
                        Date: incurredDate,
                        DateComparisonOperator: incurredDateComparisonOperator ?? 0
                      },
                IsRecoverable: isRecoverable ?? null,
                MatterNo: matterNo ?? null,
                OrderByColumns: orderByColumns ?? null,
                PageSize: pageSize ?? 25,
                StartAtIndex: startAtIndex ?? 0,
                Statuses: status ?? null
              }
            })

            const blob = new Blob([response], { type: 'text/csv' })

            const blobUrl = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = 'my_expenses_export.csv'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            URL.revokeObjectURL(blobUrl)

            resolve(response)
          } catch (error) {
            console.error('Error fetching extended expenses:', error)
            reject(error)
            return
          }
        }
      })
    })
  },
  fetchExpenseDetails: async ({ expenseClaimId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchExpenseDetails-${expenseClaimId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'accounts',
              endpoint: '/ExpenseClaim/GetExtendedById',
              data: { expenseClaimId: expenseClaimId }
            })

            resolve(response)
          } catch (error) {
            console.error('Error fetching expense details:', error)
            reject(error)
            return
          }
        }
      })
    })
  },
  fetchExpenseClaimLocationByExpenseClaimId: async ({ expenseClaimId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchExpenseClaimLocation-${expenseClaimId}`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'accounts',
              endpoint: '/ExpenseClaimLocation/GetAllByExpenseClaimId',
              data: { expenseClaimId: expenseClaimId },
              sendAsUrlParams: true
            })

            resolve(response)
          } catch (error) {
            console.error('Error fetching expense claim locations:', error)
            reject(error)
            return
          }
        }
      })
    })
  },
  fetchExpenseClaimPeriodStart: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchExpenseClaimPeriodStart`,
        delay: 600,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'accounts',
              endpoint: '/ExpenseClaim/GetCurrentPeriodStartDate'
            })

            const expenseClaimPeriodStart = new Date(response)

            resolve(expenseClaimPeriodStart)
            set({ expenseClaimPeriodStart })
          } catch (error) {
            console.error('Error fetching expense claim locations:', error)
            reject(error)
            return
          }
        }
      })
    })
  },
  resetExpensesTableUpdate: () => set({ expensesTableUpdate: 0 }),
  reset: () => set(initialState)
}))
